import { AloftPrice } from '@aloft/shared-objects';
import { Improvements } from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { EXTERNAL_DEPRECIATION_TABLE_HEADERS } from '../../../../modules/commentary/consts.ts';
import { selectAppliedExternalObsolescenceAdjsDisplay } from '../adjustmentSelectors/selectExternalObsolescenceAdj.ts';
import { selectAdjustmentSettings, selectCompSequence } from '../orderSelectors.ts';
import { selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { getCommentaryTableHelperText } from '../utils/getCommentaryTableHelperText.ts';
import { priceJSONToPrice } from '../utils/priceJSONToPrice.ts';
import {
  calculateDifferenceInDepreciation,
  getDepreciationDifferenceString,
  getDepreciationRowLabel,
} from './utils.ts';

const getExternalDepreciationRow = (
  index: number,
  improvements: Improvements,
  depreciationDifference?: AloftPrice | string,
  appliedAdjustment?: string,
) => ({
  label: getDepreciationRowLabel(index),
  externalObsolescence: priceJSONToPrice(improvements?.externalDepreciation)?.toString(true) ?? '-',
  depreciationDifference: getDepreciationDifferenceString(depreciationDifference),
  appliedAdjustment: appliedAdjustment ?? '-',
});

const selectExternalDepreciationCommentaryData = createSelector(
  [
    selectCompSequence,
    selectImprovementsEntities,
    selectAppliedExternalObsolescenceAdjsDisplay,
    selectAdjustmentSettings,
  ],
  (compSequence, improvementsEntities, appliedExternalObsolescenceAdjs, adjustmentSettings) => ({
    helperText: getCommentaryTableHelperText(adjustmentSettings, 'externalObsolescence'),
    data: ['subject', ...compSequence].map((id, index) => {
      const improvements = improvementsEntities[id];
      if (!improvements) throw new Error('error');

      const depreciationDifference =
        id === 'subject'
          ? '-'
          : calculateDifferenceInDepreciation(
              improvementsEntities.subject?.externalDepreciation,
              improvementsEntities?.[id]?.externalDepreciation,
            );

      return getExternalDepreciationRow(
        index,
        improvements,
        depreciationDifference,
        id === 'subject' ? '-' : appliedExternalObsolescenceAdjs?.[id]?.value,
      );
    }),
  }),
);

export const selectExternalDepreciationCommentaryTableDisplay = createSelector(
  [selectExternalDepreciationCommentaryData],
  ({
    data,
    helperText,
  }): {
    helperText?: string;
    data: [string[], ...string[][]];
  } => ({
    helperText,
    data: [
      EXTERNAL_DEPRECIATION_TABLE_HEADERS,
      ...data.map(({ label, externalObsolescence, depreciationDifference, appliedAdjustment }) => [
        label,
        externalObsolescence,
        depreciationDifference,
        appliedAdjustment,
      ]),
    ],
  }),
);
